import { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useParams } from 'react-router-dom'
import { Box, CircularProgress } from '@mui/material'
import { getReportParams, getReportParamsOptions, runReport } from 'store/report/reports'
import * as snackbar from 'utils/snackbar'
import ReportHeader from './ReportHeader'
import ReportParams from './ReportParams'
import ReportResult from './ReportResult'
import ReportExportPDF from './PDF'

export default function ReportRun() {
    const dispatch = useDispatch()
    const { reportId } = useParams()
    const { params: paramsDataFromStore } = useSelector(state => state.report.reports)
    const [pdfOpening, setPdfOpening] = useState(false)

    const [paramsData, setParamsData] = useState([])
    const [selectedValues, setSelectedValues] = useState({})
    const [tableData, setTableData] = useState([])
    const [tableDataLoading, setTableDataLoading] = useState(false)
    const [cols, setCols] = useState([])
    const [readyToRender, setReadyToRender] = useState(false)
    const { list } = useSelector(state => state.report.reports)
    const isCardReport = list?.some(r => r.id === Number(reportId) && Array.isArray(r.format) && r.format.some(f => f.toLowerCase() === 'pdf'))
    
    useEffect(() => {
        let cancelled = false
        const load = async () => {
            setReadyToRender(false)
            setSelectedValues({})
            setParamsData([])
            setTableData([])
            const payload = await dispatch(getReportParams({ id: reportId })).unwrap()
            if (cancelled) return
            if (payload?.parameters?.length) {
                setParamsData(payload.parameters)
            }
            setReadyToRender(true)
        }
        load()
        return () => { cancelled = true }
    }, [dispatch, reportId])

    //Reset field without re-running the whole report
    const handleRowReset = responseId => {
        setTableData(prev => ({
            ...prev,
            result: (prev?.result ?? []).map(row =>
                String(row?._reset) === String(responseId)
                    ? { ...row, response: null, signed_by: null, signature: null, signature_date: null, _reset: null }
                    : row,
            ),
        }))
    }

    useEffect(() => {
        if (tableData) {
            setCols(
                Array.isArray(tableData?.result) && tableData.result.length > 0
                    ? Object.keys(tableData.result[0] ?? {})
                    : []
            )
        }
    }, [tableData])

const handleSelect = async (paramObj, selectedOption, reason) => {
    if (reason === 'clear') {
        setSelectedValues(prev => {
            const copy = { ...prev }
            delete copy[paramObj.param]
            return copy
        })
        setTableData([])
        return
    }

    setSelectedValues(prev => ({
        ...prev,
        [paramObj.param]: selectedOption,
    }))

    if (!paramObj.childIds?.length || selectedOption == null) return

    const res = await dispatch(
        getReportParamsOptions({
            id: paramObj.id,
            filters: { 
                [paramObj.param]: selectedOption.id ?? selectedOption 
            },
        })
    )

    const refreshedParams = res?.payload?.parameters || []
    if (!refreshedParams.length) return

    setParamsData(prev =>
        prev.map(p => refreshedParams.find(r => r.id === p.id) || p)
    )

    setSelectedValues(prev => {
        const next = { ...prev }
        paramObj.childIds.forEach(childId => {
            const child = paramsData.find(p => p.id === childId)
            if (child) next[child.param] = null
        })
        return next
    })
}

const generateReport = async () => {
        const payload = { id: reportId }

        paramsData.forEach(param => {
            const selected = selectedValues[param.param]

            if (param.ui?.type === 'checkbox') {
                payload[param.param] = !!selected
                return
            }

            if (selected == null) return


            if (param.ui?.type === 'daterange' && Array.isArray(selected)) {
                if (selected[0] && selected[1]) {
                    payload['start_date'] = selected[0].format('YYYY-MM-DD')
                    payload['end_date'] = selected[1].format('YYYY-MM-DD')
                }
            }

            else if (typeof selected === 'object' && !Array.isArray(selected)) {
                payload[param.param] = selected.id ?? selected.value ?? selected[param.param] ?? selected.code ?? selected.name
            }
            
            else {
                payload[param.param] = selected
            }
        })

        setTableDataLoading(true)
        if (isCardReport) {
            setPdfOpening(true)
           snackbar.info('Generating PDF report. This may take a moment...')
        }
        const res = await dispatch(runReport(payload))
        setTableData(res?.payload || [])
        setTableDataLoading(false)
    }

    if (!readyToRender) return (
        <Box sx={{ width: '100%', height: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
            <CircularProgress />
        </Box>
    )

    return (
        <Box>
            <ReportHeader report={paramsDataFromStore} />

            <ReportParams
                paramsData={paramsData}
                selectedValues={selectedValues}
                tableDataLoading={tableDataLoading || pdfOpening}
                onSelect={handleSelect}
                onGenerate={generateReport}
            />

            {isCardReport ? (
                tableData && (
                    <ReportExportPDF 
                        data={tableData} 
                        exportNow={true} 
                        loading={tableDataLoading || pdfOpening}
                        onPdfOpened={() => setPdfOpening(false)}
                    />
                )
            ) : (
                <ReportResult
                    loading={tableDataLoading}
                    tableData={tableData?.result}
                    cols={cols}
                    selectedValues={selectedValues}
                    onRowReset={handleRowReset}
                />
            )}
        </Box>
    )
}
